// deno-lint-ignore-file no-explicit-any

export function NoOp(..._args: unknown[]) {}

export function uniq<T>(v: T, i: number, s: T[]) {
  return s.indexOf(v) === i;
}

export function notNull(obj: unknown) {
  return obj != null;
}

export function clone<T>(obj: T): T {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (_e) {
    return obj;
  }
}

//#region arr
export function A(n: number, idx?: boolean) {
  const res = Array(n).fill(0);

  return idx ? res.map((_, i) => i + 1) : res;
}

export type FnArrFill<T> = (val: null, idx: number) => T;
export function FillArr<T>(n: number, cb: FnArrFill<T>) {
  new Array(n).fill(null).map(cb);
}

export function zip(arr: any[], ...arrs: any[]) {
  return arr.map((val, i) => arrs.reduce((a, arr) => [...a, arr[i]], [val]));
}

export function chunk<T>(array: T[], chunkSize = 1) {
  const res: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    res.push(array.slice(i, i + chunkSize));
  }
  return res;
}

export type mapFn<T, K> = (e: T, i: number, a: T[]) => K;
export type mapBool<T> = mapFn<T, boolean>;
export type mapObjFn<V, K, R> = (value: V, key: K, index?: number, arr?: [K, V][]) => R;
export type mapObjFnBool<K, V> = mapObjFn<V, K, boolean>;

export function groupBy<T, K extends keyof any>(key: mapFn<T, K>, arr: T[]) {
  return arr.reduce(
    (groups, item, i, a) => {
      (groups[key(item, i, a)] ||= []).push(item);
      return groups;
    },
    {} as Record<K, T[]>,
  );
}

export function joinEach(a: string[], b: string[], sep: string = "") {
  return a.map((f, i) => f + sep + b[i]);
}

export function FindFirstCommonElement<T>(a: T[], b: T[]) {
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (a[i] === b[j]) {
        return true;
      }
    }
  }
  return false;
}

export function addOrReplace<T>(arr: T[], el: T, find?: mapBool<T>) {
  const fn = find || ((e: T) => el === e);
  const idx = arr.findIndex(fn);
  if (idx === -1) {
    arr.push(el);
  } else {
    arr[idx] = el;
  }

  return arr;
}

export function interleave(arr: any[], thing: any) {
  return ([] as any[]).concat(...arr.map((n) => [n, thing])).slice(0, -1);
}

//#endregion

//#region obj

export function mapObjIndexed<V, K extends string, R>(fn: mapObjFn<V, K, R>, obj: Record<K, V>) {
  const arr = Object.entries(obj) as [K, V][];
  const mapped = arr.map(([k, v], i, a) => [k, fn(v, k, i, a)]);
  const res = Object.fromEntries(mapped) as Record<K, R>;
  return res;
}

export function filterObjIndexed<V, K extends string>(fn: mapObjFnBool<K, V>, obj: Record<K, V>) {
  const arr = Object.entries(obj) as [K, V][];
  const filtered = arr.filter(([k, v], i, a) => [k, fn(v, k, i, a)]);
  const res = Object.fromEntries(filtered) as unknown as Partial<Record<K, V>>;
  return res;
}

export function arrayHasDuplicates(array: any[]) {
  return new Set(array).size !== array.length;
}

export function prefixObjectKeys(obj: any, prefix: string) {
  const rr = Object.entries(obj).map(([k, v]) => [`${prefix}${k}`, v]);
  return Object.fromEntries(rr);
}

export interface sumByProps<T> {
  key: (e: T) => string;
  val: (e: T) => number;
}

export function sumBy<T>(props: sumByProps<T>, arr: T[]) {
  return arr.reduce(
    (a, b: T) => {
      const k = props.key(b);
      const v = props.val(b);
      a[k] = (a[k] || 0) + v;
      return a;
    },
    {} as {
      [key: string]: number;
    },
  );
}

export function CasePascalToCamel(key: string) {
  return (key.charAt(0).toLowerCase() + key.slice(1) || key).toString();
}

export function ObjRecursiveUpdateKeys(item: unknown, keyUpdate: (k: string) => string): unknown {
  if (Array.isArray(item)) {
    return item.map((el: unknown) => ObjRecursiveUpdateKeys(el, keyUpdate));
  } else if (typeof item === "function" || item !== Object(item)) {
    return item;
  }

  return Object.fromEntries(
    Object.entries(item as Record<string, unknown>).map(([key, value]: [string, unknown]) => [
      keyUpdate(key),
      ObjRecursiveUpdateKeys(value, keyUpdate),
    ]),
  );
}

export function ObjKeysPascalToCamel(obj: unknown) {
  return ObjRecursiveUpdateKeys(obj, CasePascalToCamel);
}

export function GetAllKeysPath(obj: any, prev = "") {
  const paths: string[] = [];

  for (const key in obj) {
    const path = prev + (prev ? "." : "") + key;

    if (Array.isArray(obj[key])) {
      obj[key].forEach((c: any, i: number) => {
        if (typeof c === "object") {
          paths.push(...GetAllKeysPath(c, path + `[${i}]`));
        } else {
          paths.push(path + `[${i}]`);
        }
      });
    } else if (typeof obj[key] == "object") {
      paths.push(...GetAllKeysPath(obj[key], path));
    } else {
      paths.push(path);
    }
  }

  return paths;
}

//#endregion

//#region misc/plumbing
export function intOrDefault(x: any, defaultVal: number) {
  try {
    const res = parseInt(x, 10);
    if (Number.isInteger(res)) return res;
    return defaultVal;
  } catch (_e) {
    return defaultVal;
  }
}

//#endregion

//#region number
export const _log = (n: number, logBase = 10) => {
  return Math.log(n) / Math.log(logBase);
};

export function lerp(value1: number, value2: number, amount: number) {
  amount = amount < 0 ? 0 : amount;
  amount = amount > 1 ? 1 : amount;
  return value1 + (value2 - value1) * amount;
}

// export function clamp(input: number, min: number, max: number): number {
//   return input < min ? min : input > max ? max : input;
// }

export function toNumber(value: any) {
  const float = parseFloat(value);
  return isNaN(float) ? 0 : float;
}

export function clamp(num: number, lower: number, upper: number) {
  return Math.min(Math.max(num, lower), upper);
}

export const round = (number: number, digits = 0, base = Math.pow(10, digits)): number => {
  return Math.round(base * number) / base;
};

export const isInteger =
  Number.isInteger ||
  // eslint-disable-next-line func-names
  function (value) {
    return typeof value === "number" && isFinite(value) && Math.floor(value) === value;
  };

export function map(
  current: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
): number {
  const mapped: number = ((current - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
  return clamp(mapped, outMin, outMax);
}

export interface Range {
  min: number;
  max: number;
}
export interface CreateMapperProps {
  in: Range;
  out: Range;
}
export function CreateMapper(props: CreateMapperProps) {
  return (n: number) => {
    return map(n, props.in.min, props.in.max, props.out.min, props.out.max);
  };
}
//#endregion

//#region ring buffer

//TODO review usage
export interface Ring {
  (arg: any): void;
  pick: <T>(a: T[]) => T;
  vals: () => any[];
}

export const ring = <T,>(size: number) => {
  let ptr = 0;
  let arr: T[] = new Array(size).fill(null);

  const push = (i: T) => {
    arr[ptr] = i;
    ptr = (ptr + 1) % size;
  };

  const clear = () => {
    arr = new Array(size).fill(null);
  };

  const interf = {
    push,
    clear,
    vals: () => arr,
  };

  Object.assign(push, interf);
  Object.setPrototypeOf(push, Object.getPrototypeOf(interf));

  return push as Ring;
};

//#endregion

export function sum(a: number, b: number) {
  return a + b;
}

export function arrayMove(arr: any[], oldIndex: number, newIndex: number) {
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr; // for testing
}

export function valToPath(o: string | Record<string, any>): string {
  if (typeof o === "string") return "." + o;
  const k = Object.keys(o)[0];
  return `.${k}${valToPath(o[k])}`;
}

export function listToMap2<T1, T2 = T1>(
  list: T1[],
  keyAccesFn: (element: T1) => string,
  valueAccesFn: (element: T1) => T2 = ((element: T1) => element) as any,
): Map<string, T2> {
  const reducer = (accumulator: Map<string, T2>, currentValue: T1) =>
    accumulator.set(keyAccesFn(currentValue), valueAccesFn(currentValue));
  return list.reduce(reducer, new Map<string, T2>());
}

listToMap2([""], (k) => k); // Map<string, string>
listToMap2(
  [""],
  (k) => k,
  (k) => k.length,
); //Map<string, number>

export function pathToObject(paths: string[], separator: string = "/"): Record<string, any> {
  const result: Record<string, any> = {};

  for (const path of paths) {
    const parts = path.split(separator);
    let current = result;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) {
        current[part] = path;
      } else {
        current[part] = current[part] || {};
        current = current[part];
      }
    }
  }

  return result;
}

export function cx(...args: any[]) {
  return args.flat(Infinity).join(" ");
}

export function fmod(x: number, y: number) {
  // http://kevin.vanzonneveld.net
  // *     example 1: fmod(5.7, 1.3);
  // *     returns 1: 0.5
  let tmp: RegExpMatchArray;
  let p = 0;
  let pY = 0;
  let l = 0.0;
  let l2 = 0.0;

  tmp = x.toExponential().match(/^.\.?(.*)e(.+)$/)!;

  p = parseInt(tmp[2], 10) - (tmp[1] + "").length;
  tmp = y.toExponential().match(/^.\.?(.*)e(.+)$/)!;
  pY = parseInt(tmp[2], 10) - (tmp[1] + "").length;

  if (pY > p) {
    p = pY;
  }

  const tmp2 = x % y;

  if (p < -100 || p > 20) {
    // toFixed will give an out of bound error so we fix it like this:
    l = Math.round(Math.log(tmp2) / Math.log(10));
    l2 = Math.pow(10, l);
    const res = Number((tmp2 / l2).toFixed(l - p)) * l2;

    return res;
  } else {
    return parseFloat(tmp2.toFixed(-p));
  }
}

export function sign(x: number) {
  if (x === 0) {
    return 1;
  } else {
    return Math.abs(x) / x;
  }
}

export function tanh(n: number) {
  return (Math.exp(n) - Math.exp(-n)) / (Math.exp(n) + Math.exp(-n));
}

export function RootMeanSquare(buffer: Float32Array) {
  let sumOfSquares = 0;
  for (let i = 0; i < buffer.length; i++) {
    const val = buffer[i];
    sumOfSquares += val * val;
  }
  const rootMeanSquare = Math.sqrt(sumOfSquares / buffer.length);
  return rootMeanSquare;
}

/** flatten array of chunks into single float32array */
export function float32Flatten(chunks: Float32Array[]) {
  //get the total number of frames on the new float32array
  const nFrames = chunks.reduce((acc, elem) => acc + elem.length, 0);

  //create a new float32 with the correct number of frames
  const result = new Float32Array(nFrames);

  //insert each chunk into the new float32array
  let currentFrame = 0;
  chunks.forEach((chunk) => {
    result.set(chunk, currentFrame);
    currentFrame += chunk.length;
  });
  return result;
}
